import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Plus, Upload, ChevronDown, Eye, Edit, Package, Power, Settings, FileDown, Tag, Gift, RefreshCw, Filter, Trash2 } from 'lucide-react';
import ProductViewModal from './ProductViewModal';
import ProductEditModal from './ProductEditModal';
import Avatar from '../../../../components/common/Avatar';
import ImportResultsModal from './ImportResultsModal';
import DataTable from '../../../../components/common/DataTable';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import ConfirmationModal from '../../../../components/common/Modal/ConfirmationModal';
import { getAllProducts, createProduct, updateProduct, importProducts, handleApiError, toggleProductStatus, getProductCategories } from '../../../../services/api/productApi';
import { validateExcelFile, downloadProductImportTemplate, handleExportProductsExcel } from '../../../../utils/excelUtils';
import { useAuth } from '../../../../context/AuthContext';

const ProductsView = () => {
    const { user } = useAuth();
    const isSalesPerson = user?.primaryRole === 'SALES_PERSON';
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewProduct, setViewProduct] = useState(null);
    const [editProduct, setEditProduct] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showBulkActions, setShowBulkActions] = useState(false);
    const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [categories, setCategories] = useState(['All']);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
    const [statusTab, setStatusTab] = useState('ACTIVE');
    const [bulkActionModal, setBulkActionModal] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [sortField, setSortField] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');

    const [importResults, setImportResults] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const fileInputRef = useRef(null);
    const isFetchingRef = useRef(false);
    const optionsDropdownRef = useRef(null);
    const categoryDropdownRef = useRef(null);

    // Load products and categories on mount
    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            const data = await getProductCategories();
            setCategories(['All', ...data.filter(Boolean)]);
        } catch (err) {
            console.error('Error loading product categories:', err);
        }
    };

    // Load products from API on mount and dependencies change
    useEffect(() => {
        // Simple debounce for search
        const delay = setTimeout(() => {
            loadProducts();
        }, 300);
        return () => clearTimeout(delay);
    }, [statusTab, currentPage, pageSize, searchTerm, selectedCategory, sortField, sortDirection]);

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (optionsDropdownRef.current && !optionsDropdownRef.current.contains(event.target)) {
                setShowOptionsDropdown(false);
            }
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
                setShowCategoryDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadProducts = async () => {
        if (isFetchingRef.current) {
            console.log('Products fetch already in progress, skipping...');
            return;
        }

        isFetchingRef.current = true;
        try {
            setLoading(true);
            setError(null);
            const data = await getAllProducts({
                page: currentPage - 1,
                size: pageSize,
                search: searchTerm,
                category: selectedCategory === 'All' ? '' : selectedCategory,
                status: statusTab,
                sort: `${sortField},${sortDirection}`
            });
            console.log('Products loaded:', data.content?.length);
            setProducts(data.content || []);
            setTotalElements(data.totalElements || 0);
            setTotalPages(data.totalPages || 0);
        } catch (err) {
            console.error('Error loading products:', err);
            setError(handleApiError(err));
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    };

    // Filtered products are now fully loaded from the server
    const filteredProducts = products;

    const getCategoryColor = (category) => {
        const colors = {
            'Produce': 'bg-blue-100 text-blue-700',
            'Grains': 'bg-purple-100 text-purple-700',
            'Oil': 'bg-cyan-100 text-cyan-700',
            'Meat': 'bg-red-100 text-red-700',
            'Spices': 'bg-orange-100 text-orange-700'
        };
        return colors[category] || 'bg-gray-100 text-gray-700';
    };

    const handleSaveProduct = async (updatedProduct) => {
        try {
            if (updatedProduct.id) {
                // Update existing
                await updateProduct(updatedProduct.id, updatedProduct);
            } else {
                // Create new
                await createProduct(updatedProduct);
            }
            // Reload to get complete data from backend
            await loadProducts();
            // Reload categories list
            await loadCategories();
        } catch (err) {
            console.error('Error saving product:', err);
            throw new Error(handleApiError(err));
        }
    };

    const handleViewProduct = (product) => {
        setViewProduct(product);
    };

    const handleEditProduct = (product) => {
        setEditProduct(product);
    };

    const handleAddProduct = () => {
        setEditProduct({
            id: null,
            name: '',
            category: 'Produce',
            image: null,
            iconClass: '',
            iconBg: 'bg-blue-100',
            iconColor: 'text-blue-600',
            price: 0,
            unit: '',
            stock: 0,
            retailPrice: 0
        });
    };

    const handleDeleteProduct = (product) => {
        // Soft-delete: toggle status without confirmation modal
        handleToggleStatus(product);
    };

    const handleToggleStatus = async (product) => {
        try {
            const newActive = !product.active;
            await toggleProductStatus(product.id, newActive);
            await loadProducts();
        } catch (err) {
            alert(handleApiError(err));
        }
    };

    // Bulk selection handlers
    const toggleSelectProduct = (productId) => {
        setSelectedProducts(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            } else {
                return [...prev, productId];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedProducts.length === filteredProducts.length) {
            // Deselect all
            setSelectedProducts([]);
        } else {
            // Select all filtered products
            setSelectedProducts(filteredProducts.map(p => p.id));
        }
    };

    const handleBulkDelete = () => {
        setIsBulkDeleteModalOpen(true);
    };

    const confirmBulkDelete = () => {
        setProducts(prev => prev.filter(p => !selectedProducts.includes(p.id)));
        setSelectedProducts([]);
        setShowBulkActions(false);
        setIsBulkDeleteModalOpen(false);
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file
        const validation = validateExcelFile(file);
        if (!validation.valid) {
            alert(validation.errors.join('\n'));
            event.target.value = ''; // Reset input
            return;
        }

        // Import file
        setIsImporting(true);
        try {
            const results = await importProducts(file);
            setImportResults(results);
            // Refresh products list if any were successful
            if (results.successfulRows > 0) {
                await loadProducts();
                await loadCategories();
            }
        } catch (error) {
            console.error('Import error:', error);
            alert('Failed to import inventory. Please try again.');
        } finally {
            setIsImporting(false);
            event.target.value = ''; // Reset input
        }
    };

    const handleExportExcel = async (dataToExport = products, isSelection = false) => {
        try {
            await handleExportProductsExcel(dataToExport, user?.supplierName || 'Company');
            setShowOptionsDropdown(false);
        } catch (err) {
            alert('Export failed');
        }
    };

    const handleExportAll = async () => {
        try {
            setLoading(true);
            const exportSize = totalElements > 0 ? totalElements : 2000;
            const data = await getAllProducts({
                page: 0,
                size: exportSize,
                search: searchTerm,
                status: statusTab,
                sort: `${sortField},${sortDirection}`
            });
            
            const allRecords = data.content || [];
            
            if (allRecords.length === 0) {
                alert('No records found to export');
                return;
            }

            await handleExportProductsExcel(allRecords, user?.supplierName || 'Company');
            setShowOptionsDropdown(false);
        } catch (err) {
            console.error('Export failed:', err);
            alert('Export failed: ' + handleApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const handleBulkAction = (action) => {
        setShowOptionsDropdown(false);
        if (action === 'export') {
            const productsToExport = selectedProducts.length > 0 
                ? products.filter(p => selectedProducts.includes(p.id))
                : products;
            handleExportProductsExcel(productsToExport, user?.supplierName || 'yaseero');
        } else {
            setBulkActionModal(action);
        }
    };

    // Search filter function
    const searchFilter = (product, searchTerm) => {
        return product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.category.toLowerCase().includes(searchTerm.toLowerCase());
    };

    // Table columns configuration
    const columns = [
        ...(!isSalesPerson ? [{
            header: (
                <input
                    type="checkbox"
                    checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                />
            ),
            field: null,
            sortable: false
        }] : []),
        {
            header: 'Name',
            field: 'name',
            sortable: true
        },
        {
            header: 'Category',
            field: 'category',
            sortable: true
        },
        {
            header: 'Wholesale',
            field: 'price',
            sortable: true
        },
        {
            header: 'Retail',
            field: 'retailPrice',
            sortable: true
        },
        {
            header: 'Stock',
            field: 'stock',
            sortable: true
        },
        {
            header: 'ACTIONS',
            field: null,
            sortable: false
        }
    ];

    // Render table row
    const renderRow = (product) => (
        <tr key={product.id} className="hover:bg-blue-50/30 transition-colors group">
            {!isSalesPerson && (
                <td className="px-3 py-2">
                    <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => toggleSelectProduct(product.id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                    />
                </td>
            )}
            <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                    <Avatar name={product.name} image={product.image} size="sm" shape="circle" />
                    <div>
                        <div className="font-semibold text-[11px] text-gray-900">{product.name}</div>
                        <div className="text-[10px] text-gray-500">{product.unit}</div>
                    </div>
                </div>
            </td>
            <td className="px-3 py-2">
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${getCategoryColor(product.category)}`}>
                    {product.category}
                </span>
            </td>
            <td className="px-3 py-2">
                <div className="font-bold text-[11px] text-gray-900">AED {product.price.toFixed(2)}</div>
                <div className="text-[10px] text-gray-500">{product.unit}</div>
            </td>
            <td className="px-3 py-2">
                <div className="font-bold text-[11px] text-gray-900">AED {product.retailPrice.toFixed(2)}</div>
                <div className="text-[10px] text-gray-500">{product.unit}</div>
            </td>
            <td className="px-3 py-2">
                <div className={`font-bold text-[11px] ${product.stock > 100 ? 'text-green-600' : product.stock > 50 ? 'text-orange-600' : 'text-red-600'}`}>
                    {product.stock} {product.unit}
                </div>
            </td>
            <td className="px-3 py-2 text-right">
                <div className="flex items-center justify-end gap-1">
                    <button
                        onClick={() => handleViewProduct(product)}
                        className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-all"
                        title="View Item"
                    >
                        <Eye size={14} />
                    </button>
                    {!isSalesPerson && (
                        <>
                            <button
                                onClick={() => handleEditProduct(product)}
                                className="p-1.5 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-md transition-all"
                                title="Edit Item"
                            >
                                <Edit size={14} />
                            </button>
                            <button
                                onClick={() => handleToggleStatus(product)}
                                className={`p-1.5 rounded-md transition-all ${product.active ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-red-600 bg-red-50 hover:bg-red-100'}`}
                                title={product.active ? 'Deactivate' : 'Activate'}
                            >
                                <Power size={14} />
                            </button>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );

    // Render product card for mobile/tablet view
    const renderCard = (product) => (
        <div className="bg-gradient-to-br from-white to-blue-50/30 border border-gray-200 rounded-xl p-3 md:p-4 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
            {/* Header with Avatar */}
            <div className="flex items-start gap-3 mb-3">
                <Avatar name={product.name} image={product.image} size="md" shape="circle" />
                <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-sm md:text-base text-gray-900 truncate">{product.name}</h3>
                    <p className="text-xs text-gray-500">{product.unit}</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold mt-1 ${getCategoryColor(product.category)}`}>
                        {product.category}
                    </span>
                </div>
                <input
                    type="checkbox"
                    checked={selectedProducts.includes(product.id)}
                    onChange={() => toggleSelectProduct(product.id)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                />
            </div>

            {/* Price Information */}
            <div className="grid grid-cols-2 gap-2 mb-3 bg-white/60 rounded-lg p-2 border border-gray-100">
                <div>
                    <p className="text-[10px] text-gray-500 font-medium">Wholesale</p>
                    <p className="text-sm md:text-base font-bold text-blue-600">AED {product.price.toFixed(2)}</p>
                </div>
                <div>
                    <p className="text-[10px] text-gray-500 font-medium">Retail</p>
                    <p className="text-sm md:text-base font-bold text-purple-600">AED {product.retailPrice.toFixed(2)}</p>
                </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={() => handleViewProduct(product)}
                        className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all"
                        title="View Item"
                    >
                        <Eye size={16} />
                    </button>
                    {!isSalesPerson && (
                        <>
                            <button
                                onClick={() => handleEditProduct(product)}
                                className="p-2 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-all"
                                title="Edit Item"
                            >
                                <Edit size={16} />
                            </button>
                            <button
                                onClick={() => handleToggleStatus(product)}
                                className={`p-2 rounded-lg transition-all ${product.active ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-red-600 bg-red-50 hover:bg-red-100'}`}
                                title={product.active ? 'Deactivate' : 'Activate'}
                            >
                                <Power size={16} />
                            </button>
                        </>
                    )}
                </div>
                <div className="text-right">
                    <p className="text-[10px] text-gray-500">Stock</p>
                    <p className={`text-xs md:text-sm font-bold ${product.stock > 100 ? 'text-green-600' : product.stock > 50 ? 'text-orange-600' : 'text-red-600'}`}>
                        {product.stock} {product.unit}
                    </p>
                </div>
            </div>
        </div>
    );

    // Empty state
    const emptyState = (
        <div className="text-center py-12">
            <Package size={48} className="mx-auto text-gray-300 mb-3" />
            <h3 className="text-lg font-semibold text-gray-600 mb-1">No items found</h3>
            <p className="text-sm text-gray-500">Try adjusting your search or filter</p>
        </div>
    );

    return (
        <div className="flex flex-col h-full bg-gradient-to-br from-blue-50 via-white to-purple-50 p-2 overflow-hidden">
            {/* Header */}
            <div className="mb-2">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            Inventory
                        </h1>
                        <p className="text-gray-500 text-[10px]">Manage your inventory items</p>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <button
                            onClick={loadProducts}
                            disabled={loading}
                            className="p-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-all flex items-center justify-center focus-visible:ring-2 focus-visible:ring-blue-500 focus:outline-none disabled:opacity-50 shrink-0"
                            title="Refresh inventory"
                            aria-label="Refresh inventory list"
                        >
                            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
                        </button>
                        {!isSalesPerson && (
                            <>
                                {/* Options Dropdown */}
                                <div className="relative" ref={optionsDropdownRef}>
                                    <button
                                        onClick={() => setShowOptionsDropdown(!showOptionsDropdown)}
                                        className="px-2.5 sm:px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-1.5 sm:gap-2 font-semibold text-xs sm:text-sm shrink-0"
                                    >
                                        <Settings size={18} />
                                        <span className="hidden sm:inline">Options</span>
                                        <ChevronDown size={14} className={`transition-transform ${showOptionsDropdown ? 'rotate-180' : ''}`} />
                                    </button>
                                    {showOptionsDropdown && (
                                        <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden">
                                            <button
                                                onClick={() => {
                                                    handleExportAll();
                                                    setShowOptionsDropdown(false);
                                                }}
                                                className="w-full text-left px-4 py-3 text-sm transition-colors text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                                            >
                                                <FileDown size={16} />
                                                Export All
                                            </button>
                                            <div className="border-t border-gray-100"></div>
                                            <button
                                                onClick={() => {
                                                    handleBulkAction('export');
                                                    setShowOptionsDropdown(false);
                                                }}
                                                disabled={selectedProducts.length === 0}
                                                className="w-full text-left px-4 py-3 text-sm transition-colors text-gray-700 hover:bg-gray-50 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <FileDown size={16} />
                                                Export Selected ({selectedProducts.length})
                                            </button>
                                            <div className="border-t border-gray-200"></div>
                                            <button
                                                onClick={() => {
                                                    downloadProductImportTemplate();
                                                    setShowOptionsDropdown(false);
                                                }}
                                                className="w-full text-left px-4 py-3 text-sm transition-colors text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                                            >
                                                <FileDown size={16} />
                                                Download Template
                                            </button>
                                            <button
                                                onClick={() => {
                                                    handleImportClick();
                                                    setShowOptionsDropdown(false);
                                                }}
                                                disabled={isImporting}
                                                className="w-full text-left px-4 py-3 text-sm transition-colors text-gray-700 hover:bg-gray-50 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <Upload size={16} />
                                                {isImporting ? 'Importing...' : 'Import Excel'}
                                            </button>
                                            <div className="border-t border-gray-200"></div>
                                            <button
                                                onClick={() => {
                                                    handleBulkAction('itemGroup');
                                                    setShowOptionsDropdown(false);
                                                }}
                                                className="w-full text-left px-4 py-3 text-sm transition-colors text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                                            >
                                                <Tag size={16} />
                                                Item Group {selectedProducts.length > 0 ? `(${selectedProducts.length})` : ''}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    handleBulkAction('addOffer');
                                                    setShowOptionsDropdown(false);
                                                }}
                                                className="w-full text-left px-4 py-3 text-sm transition-colors text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                                            >
                                                <Gift size={16} />
                                                Add Offer {selectedProducts.length > 0 ? `(${selectedProducts.length})` : ''}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    handleBulkAction('bulkUpdate');
                                                    setShowOptionsDropdown(false);
                                                }}
                                                className="w-full text-left px-4 py-3 text-sm transition-colors text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                                            >
                                                <RefreshCw size={16} />
                                                Bulk Update {selectedProducts.length > 0 ? `(${selectedProducts.length})` : ''}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />

                                <button
                                    onClick={() => handleExportExcel()}
                                    className="px-2.5 sm:px-4 py-2 bg-white border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 transition-all flex items-center justify-center gap-1.5 sm:gap-2 font-bold text-xs sm:text-sm shadow-sm shrink-0"
                                    title="Export PDF"
                                >
                                    <FileDown size={18} />
                                    <span className="hidden sm:inline">Export PDF</span>
                                </button>

                                <button
                                    className="px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-1.5 sm:gap-2 font-semibold text-xs sm:text-sm shrink-0"
                                    onClick={handleAddProduct}
                                    title="Add Item"
                                >
                                    <Plus size={18} />
                                    <span className="hidden sm:inline">Add Item</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Status Tabs */}
            <div className="px-2 sm:px-4 bg-white border-b flex items-center justify-between gap-2 overflow-x-auto no-scrollbar">
                <div className="flex gap-4 sm:gap-8 shrink-0">
                    <button 
                        className={`py-2.5 sm:py-3 px-1 text-xs sm:text-sm font-bold border-b-2 transition-all flex items-center gap-1.5 sm:gap-2 ${statusTab === 'ACTIVE' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`}
                        onClick={() => {
                            setStatusTab('ACTIVE');
                            setCurrentPage(1);
                        }}
                    >
                        <Package size={15} />
                        Active Inventory
                    </button>
                    <button 
                        className={`py-2.5 sm:py-3 px-1 text-xs sm:text-sm font-bold border-b-2 transition-all flex items-center gap-1.5 sm:gap-2 ${statusTab === 'INACTIVE' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`}
                        onClick={() => {
                            setStatusTab('INACTIVE');
                            setCurrentPage(1);
                        }}
                    >
                        <Power size={15} className="rotate-180" />
                        Inactive Inventory
                    </button>
                </div>

                <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                    {(searchTerm || selectedCategory !== 'All') && (
                        <button
                            onClick={() => {
                                setSearchTerm('');
                                setSelectedCategory('All');
                                setCurrentPage(1);
                            }}
                            className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-700 font-bold uppercase tracking-wider whitespace-nowrap"
                        >
                            <RefreshCw size={12} />
                            Reset
                        </button>
                    )}
                    {/* Category Filter */}
                    <div className="relative" ref={categoryDropdownRef}>
                        <button
                            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                            className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-all font-semibold text-gray-700 whitespace-nowrap"
                        >
                            <Filter size={14} />
                            <span className="hidden sm:inline">Category: </span>{selectedCategory}
                            <ChevronDown size={14} className={`transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                        </button>
                        {showCategoryDropdown && (
                            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-xl z-20 overflow-hidden py-1">
                                {categories.map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => {
                                            setSelectedCategory(cat);
                                            setCurrentPage(1);
                                            setShowCategoryDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${selectedCategory === cat
                                            ? 'bg-blue-50 text-blue-600 font-semibold'
                                            : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="flex-1 min-h-0 overflow-hidden mb-1">
                <DataTable
                    data={filteredProducts}
                    columns={columns}
                    renderRow={renderRow}
                    renderCard={renderCard}
                    searchPlaceholder="Search inventory by name or category..."
                    emptyState={emptyState}
                    defaultItemsPerPage={pageSize}
                    serverSide={true}
                    totalElements={totalElements}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    loading={loading}
                    error={error}
                    searchTerm={searchTerm}
                    onPageChange={setCurrentPage}
                    onSearch={(val) => {
                        setSearchTerm(val);
                        setCurrentPage(1);
                    }}
                    onSort={(field, dir) => {
                        setSortField(field);
                        setSortDirection(dir);
                        setCurrentPage(1);
                    }}
                    onItemsPerPageChange={(size) => {
                        setPageSize(size);
                        setCurrentPage(1);
                    }}
                />
            </div>

            {/* Modals */}
            {viewProduct && (
                <ProductViewModal
                    product={viewProduct}
                    onClose={() => setViewProduct(null)}
                    onEdit={handleEditProduct}
                />
            )}

            {editProduct && (
                <ProductEditModal
                    product={editProduct}
                    onClose={() => setEditProduct(null)}
                    onSave={handleSaveProduct}
                />
            )}

            <ConfirmationModal
                isOpen={isBulkDeleteModalOpen}
                onClose={() => setIsBulkDeleteModalOpen(false)}
                onConfirm={confirmBulkDelete}
                title="Delete Items?"
                message={`Are you sure you want to delete ${selectedProducts.length} selected items? This action cannot be undone.`}
                confirmText="Yes, Delete All"
                cancelText="Cancel"
                type="danger"
                icon={<Trash2 className="w-6 h-6 text-red-600" />}
            />

            <ImportResultsModal
                results={importResults}
                onClose={() => setImportResults(null)}
            />

            {bulkActionModal && (
                <ProductBulkModal
                    action={bulkActionModal}
                    selectedIds={selectedProducts}
                    products={products}
                    categories={categories}
                    onClose={() => setBulkActionModal(null)}
                    onRefresh={async () => {
                        await loadProducts();
                        await loadCategories();
                        setSelectedProducts([]);
                    }}
                />
            )}
        </div>
    );
};

const ProductBulkModal = ({ action, selectedIds, products, categories, onClose, onRefresh }) => {
    const targetProducts = selectedIds.length > 0
        ? products.filter(p => selectedIds.includes(p.id))
        : products;

    const [category, setCategory] = useState('');
    const [offerDiscountPercent, setOfferDiscountPercent] = useState('');
    const [discountedWholesale, setDiscountedWholesale] = useState('');
    const [discountedRetail, setDiscountedRetail] = useState('');
    
    // Bulk update fields
    const [updateField, setUpdateField] = useState('category');
    const [updateValue, setUpdateValue] = useState('');
    
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErr('');

        try {
            for (const prod of targetProducts) {
                let updatedData = { ...prod };

                if (action === 'itemGroup') {
                    if (!category.trim()) throw new Error('Please select or enter a group/category.');
                    updatedData.category = category.trim();
                } else if (action === 'addOffer') {
                    if (offerDiscountPercent) {
                        const pct = parseFloat(offerDiscountPercent) / 100;
                        updatedData.discountedPrice = Math.max(0, (prod.price || 0) * (1 - pct));
                        updatedData.discountedRetailPrice = Math.max(0, (prod.retailPrice || 0) * (1 - pct));
                    } else {
                        if (discountedWholesale !== '') updatedData.discountedPrice = parseFloat(discountedWholesale) || 0;
                        if (discountedRetail !== '') updatedData.discountedRetailPrice = parseFloat(discountedRetail) || 0;
                    }
                } else if (action === 'bulkUpdate') {
                    if (updateField === 'category') updatedData.category = updateValue;
                    else if (updateField === 'price') updatedData.price = parseFloat(updateValue) || 0;
                    else if (updateField === 'retailPrice') updatedData.retailPrice = parseFloat(updateValue) || 0;
                    else if (updateField === 'stock') updatedData.stock = parseFloat(updateValue) || 0;
                    else if (updateField === 'active') updatedData.active = updateValue === 'true';
                }

                await updateProduct(prod.id, updatedData);
            }

            await onRefresh();
            onClose();
        } catch (error) {
            console.error('Bulk update error:', error);
            setErr(error.message || handleApiError(error));
        } finally {
            setSaving(false);
        }
    };

    const titleMap = {
        itemGroup: 'Assign Item Group / Category',
        addOffer: 'Add Offer / Promotional Discount',
        bulkUpdate: 'Bulk Update Inventory'
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-slideUp" onClick={e => e.stopPropagation()}>
                <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
                    <h3 className="font-bold text-gray-900 text-base">{titleMap[action]}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 font-bold text-lg">✕</button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <p className="text-xs font-semibold text-blue-600 bg-blue-50 p-2.5 rounded-lg border border-blue-100">
                        Applying to {targetProducts.length} product{targetProducts.length !== 1 ? 's' : ''} {selectedIds.length > 0 ? '(Selected)' : '(All visible)'}
                    </p>

                    {err && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl">{err}</div>}

                    {action === 'itemGroup' && (
                        <div>
                            <label className="block text-xs font-bold text-gray-700 mb-1">Target Item Group / Category</label>
                            <input
                                type="text"
                                list="category-options"
                                value={category}
                                onChange={e => setCategory(e.target.value)}
                                placeholder="Select or type item group..."
                                className="w-full px-3.5 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                required
                            />
                            <datalist id="category-options">
                                {categories.filter(c => c !== 'All').map(c => <option key={c} value={c} />)}
                            </datalist>
                        </div>
                    )}

                    {action === 'addOffer' && (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Offer Discount Percentage (%)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={offerDiscountPercent}
                                    onChange={e => setOfferDiscountPercent(e.target.value)}
                                    placeholder="E.g. 10 for 10% off"
                                    className="w-full px-3.5 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                            <div className="text-center text-[10px] text-gray-400 font-bold uppercase tracking-wider">— OR fixed offer prices —</div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Wholesale Offer Price (AED)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={discountedWholesale}
                                    onChange={e => setDiscountedWholesale(e.target.value)}
                                    placeholder="Enter offer price"
                                    className="w-full px-3.5 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Retail Offer Price (AED)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={discountedRetail}
                                    onChange={e => setDiscountedRetail(e.target.value)}
                                    placeholder="Enter offer price"
                                    className="w-full px-3.5 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                        </div>
                    )}

                    {action === 'bulkUpdate' && (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">Field to Update</label>
                                <select
                                    value={updateField}
                                    onChange={e => { setUpdateField(e.target.value); setUpdateValue(''); }}
                                    className="w-full px-3.5 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                >
                                    <option value="category">Category / Item Group</option>
                                    <option value="price">Wholesale Price</option>
                                    <option value="retailPrice">Retail Price</option>
                                    <option value="stock">Available Stock</option>
                                    <option value="active">Active Status</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 mb-1">New Value</label>
                                {updateField === 'active' ? (
                                    <select
                                        value={updateValue}
                                        onChange={e => setUpdateValue(e.target.value)}
                                        className="w-full px-3.5 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                        required
                                    >
                                        <option value="">Select Status...</option>
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                ) : updateField === 'category' ? (
                                    <input
                                        type="text"
                                        list="category-options"
                                        value={updateValue}
                                        onChange={e => setUpdateValue(e.target.value)}
                                        placeholder="Enter category name..."
                                        className="w-full px-3.5 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        required
                                    />
                                ) : (
                                    <input
                                        type="number"
                                        step="any"
                                        value={updateValue}
                                        onChange={e => setUpdateValue(e.target.value)}
                                        placeholder={`Enter new ${updateField}...`}
                                        className="w-full px-3.5 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        required
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 py-2.5 rounded-xl bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 transition-colors shadow-md disabled:opacity-50"
                        >
                            {saving ? 'Updating...' : 'Apply Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductsView;
